import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

import { Button } from 'components/Form/Button/Button';
import { Input } from 'components/Form/Input/Input/Input';
import { Radio } from 'components/Form/Radio/Radio';

import { LineItemIdentifiers } from '../LineItemIdentifiers/LineItemIdentifiers';

export type MoveDestination = { kind: 'existing'; tuId: string } | { kind: 'new' };

const NEW_UNIT_VALUE = '__new_unit__';

type MoveToDialogProps = {
  productName: string;
  identifiers: Array<string>;
  unitOfMeasure?: string;
  destinations: Array<{ tuId: string; position: string }>;
  max: number;
  onConfirm: (destination: MoveDestination, quantity: number) => void;
  onCancel: () => void;
};

/**
 * i18n keys
 * t('webedi:packing_list.transport_unit.title', { position })
 * t('webedi:packing_list.placement_dialog.available', { available })
 * t('webedi:packing_list.placement_dialog.available_with_unit', { available, unit })
 */
export const MoveToDialog = ({
  productName,
  identifiers,
  unitOfMeasure,
  destinations,
  max,
  onConfirm,
  onCancel,
}: MoveToDialogProps) => {
  const { t } = useTranslation();
  const [destination, setDestination] = useState<MoveDestination>(
    destinations[0] ? { kind: 'existing', tuId: destinations[0].tuId } : { kind: 'new' },
  );
  const [quantity, setQuantity] = useState(max);
  const [hasError, setHasError] = useState(false);
  const selectedValue = destination.kind === 'existing' ? destination.tuId : NEW_UNIT_VALUE;

  const availableLabel = unitOfMeasure
    ? t('webedi:packing_list.placement_dialog.available_with_unit', { available: max, unit: unitOfMeasure })
    : t('webedi:packing_list.placement_dialog.available', { available: max });

  const updateQuantity = (next: number) => {
    setQuantity(next);
    setHasError(false);
  };

  const handleMove = () => {
    if (!isValidQuantity(quantity, max)) {
      setHasError(true);
      return;
    }
    onConfirm(destination, quantity);
  };

  return (
    <Dialog open onClose={onCancel} className="relative z-60">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
      <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
          <p className="text-sm font-semibold text-gray-900">{t('webedi:packing_list.move_to_dialog.title')}</p>
          <p className="mt-1 text-sm text-gray-700">{productName}</p>
          <LineItemIdentifiers
            parts={[...identifiers, availableLabel]}
            className="mt-1 line-clamp-2 text-xs text-gray-500"
          />
          <Radio.Group
            label={t('webedi:packing_list.move_to_dialog.destination_label')}
            variant="list-compact"
            className="mt-4"
          >
            {destinations.map((option) => (
              <Radio.Item
                key={option.tuId}
                variant="list-compact"
                name="move-to-destination"
                value={option.tuId}
                checked={selectedValue === option.tuId}
                onChange={() => setDestination({ kind: 'existing', tuId: option.tuId })}
                label={t('webedi:packing_list.transport_unit.title', { position: option.position })}
              />
            ))}
            <Radio.Item
              variant="list-compact"
              name="move-to-destination"
              value={NEW_UNIT_VALUE}
              checked={selectedValue === NEW_UNIT_VALUE}
              onChange={() => setDestination({ kind: 'new' })}
              label={t('webedi:packing_list.move_to_dialog.new_unit')}
            />
          </Radio.Group>
          <div className="mt-4 flex items-center gap-2">
            <Button
              variant="secondary"
              size="base"
              iconOnly
              LeftIcon={MinusIcon}
              aria-label={t('webedi:packing_list.placement_dialog.decrease')}
              onClick={() => updateQuantity(Math.max(1, quantity - 1))}
              analyticsId="webedi:packing_list_move_to_decrease"
            >
              {t('webedi:packing_list.placement_dialog.decrease')}
            </Button>
            <Input
              type="number"
              aria-label={t('webedi:packing_list.placement_dialog.quantity_label')}
              value={quantity}
              fieldState={hasError ? 'error' : 'default'}
              className="text-center"
              wrapperClassName="min-w-0 flex-1"
              onChange={(event) => updateQuantity(Number(event.target.value))}
              rightAddon={
                unitOfMeasure ? <span className="shrink-0 text-sm text-gray-500">{unitOfMeasure}</span> : null
              }
            />
            <Button
              variant="secondary"
              size="base"
              iconOnly
              LeftIcon={PlusIcon}
              aria-label={t('webedi:packing_list.placement_dialog.increase')}
              onClick={() => updateQuantity(Math.min(max, quantity + 1))}
              analyticsId="webedi:packing_list_move_to_increase"
            >
              {t('webedi:packing_list.placement_dialog.increase')}
            </Button>
          </div>
          {hasError && (
            <p className="mt-2 text-sm text-red-600">
              {t('webedi:packing_list.placement_dialog.error.invalid_quantity', { available: max })}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} analyticsId="webedi:packing_list_move_to_cancel">
              {t('common:cancel')}
            </Button>
            <Button onClick={handleMove} analyticsId="webedi:packing_list_move_to_confirm">
              {t('webedi:packing_list.move_to_dialog.move')}
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

const isValidQuantity = (quantity: number, max: number): boolean =>
  Number.isInteger(quantity) && quantity >= 1 && quantity <= max;
